import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { projects as projectsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { GameController, Plus, Trash, ArrowRight, SignOut, Globe, MagnifyingGlass, Sun, Moon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { GENRES_2D, ENGINES_2D } from "../lib/gamecraft";
import CameraModePicker from "../components/gc/CameraModePicker";
import { Button } from "../components/ui/button";
import { greetingLine, displayHandle, roleLabel } from "../lib/profile";
import ProfileAvatar from "../components/gc/ProfileAvatar";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", description: "", genre: "Action-Adventure", mode: "2d", engine: "GameCraft 2D", camera: "side" });

  const load = async () => {
    setLoading(true);
    try {
      setItems(await projectsApi.list());
    } catch {
      toast.error(t("toast.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return;
    try {
      const p = await projectsApi.create({ ...form, mode: "2d", camera: form.camera, config: { camera: form.camera } });
      toast.success(t("toast.projectCreated"));
      setOpen(false);
      navigate(`/project/${p.project_id}`);
    } catch {
      toast.error(t("toast.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await projectsApi.remove(id);
      setItems((x) => x.filter((p) => p.project_id !== id));
      toast.success(t("toast.projectDeleted"));
    } catch {
      toast.error(t("toast.deleteFailed"));
    }
  };

  const filtered = items.filter((p) =>
    !q.trim() || (p.name + " " + (p.description || "") + " " + (p.genre || "")).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="gc-app min-h-screen">
      <header className="sticky top-0 z-30 px-4 pt-4">
        <div className="gc-glass max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-sky-500 text-white flex items-center justify-center">
              <GameController size={18} weight="fill" />
            </div>
            <span className="font-display font-bold">{t("app.name")}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggle} className="gc-btn gc-btn-ghost h-9 w-9 p-0">{theme === "light" ? <Moon size={16} /> : <Sun size={16} />}</button>
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} className="gc-btn gc-btn-ghost h-9 px-3 text-xs"><Globe size={14} /> {lang.toUpperCase()}</button>
            <ProfileAvatar user={user} size={32} />
            <button onClick={async () => { await logout(); navigate("/"); }} className="gc-btn gc-btn-ghost h-9 w-9 p-0 text-red-400"><SignOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-sm mb-2" style={{ color: "var(--gc-muted)" }}>
            {greetingLine(user, lang)}
            {user?.studio ? ` · ${user.studio}` : ""}
            {user?.handle ? ` · ${displayHandle(user)}` : ""}
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("dashboard.title")}</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--gc-muted)" }}>
            {user?.bio
              || (lang === "es"
                ? "Tu estudio 2D para idear, prototipar y probar desde cualquier lugar."
                : "Your 2D studio to ideate, prototype and playtest from anywhere.")}
            {user?.role ? ` · ${roleLabel(user.role)}` : ""}
          </p>
        </div>

        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gc-muted)" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar proyectos..." : "Search projects..."} className="gc-input pl-9 h-10" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="gc-btn gc-btn-primary h-10 px-4"><Plus size={14} weight="bold" /> {t("dashboard.new")}</button>
            </DialogTrigger>
            <DialogContent className="gc-card text-[var(--gc-text)] max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{t("modal.new.title")}</DialogTitle>
                <DialogDescription>{lang === "es" ? "Solo 2D. Podrás editarlo todo después." : "2D only. You can edit everything later."}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className="text-xs">{t("modal.new.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="gc-input mt-1" required />
                </div>
                <div>
                  <Label className="text-xs">{t("modal.new.desc")}</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="gc-input mt-1" rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t("modal.new.genre")}</Label>
                    <Select value={form.genre} onValueChange={(v) => setForm({ ...form, genre: v })}>
                      <SelectTrigger className="gc-input mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{GENRES_2D.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                  <Label className="text-xs">{t("modal.new.engine")}</Label>
                  <Select value={form.engine} onValueChange={(v) => setForm({ ...form, engine: v })}>
                    <SelectTrigger className="gc-input mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ENGINES_2D.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                </div>
                <div>
                  <Label className="text-xs">Cámara 2D</Label>
                  <div className="mt-1">
                    <CameraModePicker value={form.camera} onChange={(camera) => setForm({ ...form, camera })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t("modal.new.cancel")}</Button>
                  <button type="submit" className="gc-btn gc-btn-primary h-10 px-4">{t("modal.new.create")}</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="gf-blink text-sm" style={{ color: "var(--gc-muted)" }}>loading...</div>
        ) : filtered.length === 0 ? (
          <div className="gc-card px-10 py-20 flex flex-col items-center text-center">
            <GameController size={40} className="mb-4 text-sky-500" />
            <p className="text-sm" style={{ color: "var(--gc-muted)" }}>{q ? (lang === "es" ? "Sin resultados" : "No results") : t("dashboard.empty")}</p>
            {!q && <button onClick={() => setOpen(true)} className="gc-btn gc-btn-primary h-10 px-4 mt-5"><Plus size={14} /> {t("dashboard.new")}</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((p) => (
              <button
                key={p.project_id}
                onClick={() => navigate(`/project/${p.project_id}`)}
                className="gc-card gc-card-hover p-4 text-left flex items-center gap-4"
              >
                <div className="w-14 h-14 rounded-2xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: "var(--gc-bg-accent)" }}>
                  {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-full h-full object-cover" /> : <GameController size={22} className="text-sky-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-xs truncate" style={{ color: "var(--gc-muted)" }}>{p.genre} · {p.config?.camera === "topdown" ? "arriba" : "lado"} · {p.engine}</div>
                </div>
                <ArrowRight size={16} className="text-sky-500" />
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(p.project_id); }}
                  className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"
                >
                  <Trash size={14} />
                </span>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
